<template>
  <AppChip v-if="status" :color="status.color" text-color="white--text" small>
    {{ status.text }}</AppChip
  >
</template>
<script>
import gql from "fraql";
import i18next from "i18next";
export default {
  name: "AppShowHorseVaccinationStatus"
};

export function refetchHorseVaccinationStatus(horseId, checkDate = null) {
  return apolloClient.query({
    query: gql`
      query horseVaccinationCheckStatus($horseId: ID, $checkDate: Date) {
        horse(id: $horseId) {
          id
          vaccinationNationalStatusOn(checkDate: $checkDate)
          vaccinationInternationalStatusOn(checkDate: $checkDate)
        }
      }
    `,
    variables: {
      horseId,
      checkDate: checkDate || dayjs().format("YYYY-MM-DD")
    }
  });
}

export function retrieveVaccinationStatusTranslation(status) {
  switch (status) {
    case "COMPLIANT":
      return i18next.t(
        "horses:horseVaccinationStatus.COMPLIANT.label",
        "No check needed"
      );

    case "COMPLIANT_CHECKED":
      return i18next.t(
        "horses:horseVaccinationStatus.COMPLIANT_CHECKED.label",
        "Checked"
      );

    case "COMPLIANT_NOT_CHECKED":
      return i18next.t(
        "horses:horseVaccinationStatus.COMPLIANT_NOT_CHECKED.label",
        "Not checked"
      );

    case "NONCOMPLIANT":
      return i18next.t(
        "horses:horseVaccinationStatus.NONCOMPLIANT.label",
        "Check needed"
      );

    default:
      return null;
  }
}
</script>
<script setup>
import { ref, computed } from "vue";
import { useTranslation } from "@/plugins/i18next/i18next";
import dayjs from "dayjs";
import { useQuery } from "@vue/apollo-composable";
import { apolloClient } from "@/plugins/apollo";

import AppChip from "@elements/AppChip.vue";

const props = defineProps({
  horse: {
    type: Object,
    required: true
  },
  checkDate: {
    type: String,
    default: () => dayjs().format("YYYY-MM-DD")
  },
  rule: {
    type: String,
    default: "national",
    validate(v) {
      return ["national", "international"].includes(v);
    }
  }
});

const { t } = useTranslation();

const vaccinationCheck = ref(null);
const fieldName =
  props.rule == "national"
    ? "vaccinationNationalStatusOn"
    : "vaccinationInternationalStatusOn";

const status = computed(() => {
  if (!vaccinationCheck.value) return null;
  switch (vaccinationCheck.value[fieldName]) {
    case "COMPLIANT":
      return {
        color: "valid",
        text: t(
          "horses:horseVaccinationStatus.COMPLIANT.label",
          "No check needed"
        )
      };
    case "COMPLIANT_CHECKED":
      return {
        color: "valid",
        text: t(
          "horses:horseVaccinationStatus.COMPLIANT_CHECKED.label",
          "Checked"
        )
      };
    case "COMPLIANT_NOT_CHECKED":
      return {
        color: "accent",
        text: t(
          "horses:horseVaccinationStatus.COMPLIANT_NOT_CHECKED.label",
          "Not checked"
        )
      };
    case "NONCOMPLIANT":
      return {
        color: "red",
        text: t(
          "horses:horseVaccinationStatus.NONCOMPLIANT.label",
          "Check needed"
        )
      };
    default:
      return null;
  }
});

const { onResult } = useQuery(
  () => gql`
    query horseVaccinationCheckStatus($horseId: ID, $checkDate: Date) {
      horse(id: $horseId) {
        id
        ${fieldName}(checkDate: $checkDate)
      }
    }
  `,
  () => ({
    horseId: props.horse.id,
    checkDate: props.checkDate || dayjs().format("YYYY-MM-DD")
  }),
  { fetchPolicy: "cache-first" }
);

onResult(({ loading, data }) => {
  if (loading) return;
  vaccinationCheck.value = data.horse;
});
</script>
